import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Select, DatePicker, Button, Table, Tag, Space, Modal, message, Card, Col, Drawer, Row, Alert, Spin } from 'antd';
import { ExclamationCircleOutlined, UserOutlined, ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import FilterPanel from '../../../components/common/FilterPanel';
import type { PickingTaskDTO, QueryPickingTaskPageRequest, AssignPickingTaskRequest } from '../../../types/picking/pickingTask';
import type { Paging } from '../../../types/common';
import * as pickingTaskService from '../../../services/picking/pickingTaskService';
import * as accountService from '../../../services/account/accountService';
import {
  PickingTaskStatusOptions,
  TaskSourceOptions,
  PickingTaskStatus,
} from '../../../constants/picking';
import PickingTaskDetail from './components/PickingTaskDetail';

const { RangePicker } = DatePicker;

const PickingTaskList: React.FC = () => {
  const { t } = useTranslation();
  const [searchForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<PickingTaskDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({ currentPage: 1, pageSize: 20, totalCount: 0 });
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentTaskCode, setCurrentTaskCode] = useState<string | null>(null);
  
  // 指派相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<PickingTaskDTO[]>([]);
  const [assignDrawerVisible, setAssignDrawerVisible] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  
  // 导出相关状态
  const [exportLoading, setExportLoading] = useState(false);

  // Picker 远程搜索
  const [pickerOptions, setPickerOptions] = useState<{ label: string; value: string; pickerName: string }[]>([]);
  const [pickerSearching, setPickerSearching] = useState(false);
  const pickerSearchTimer = useRef<ReturnType<typeof setTimeout>>();

  const loadPickerOptions = async (keyword?: string) => {
    setPickerSearching(true);
    try {
      const params: any = { currentPage: 1, pageSize: 10 };
      if (keyword) {
        params.nameLike = keyword;
        params.loginIdLike = keyword;
      }
      const res = await accountService.queryStoreAccounts(params);
      if (res.success && res.data) {
        setPickerOptions(
          (res.data.tableData || []).map((acc: any) => ({
            label: `${acc.name || ''} (${acc.loginId})`,
            value: acc.loginId,
            pickerName: acc.name || acc.loginId,
          }))
        );
      }
    } finally {
      setPickerSearching(false);
    }
  };

  const handlePickerSearch = (keyword: string) => {
    if (pickerSearchTimer.current) clearTimeout(pickerSearchTimer.current);
    if (!keyword) {
      loadPickerOptions();
      return;
    }
    pickerSearchTimer.current = setTimeout(() => loadPickerOptions(keyword), 300);
  };

  // 可指派的任务状态（未开始或进行中）
  const canAssignStatuses = [PickingTaskStatus.NOT_STARTED, PickingTaskStatus.IN_PROGRESS];

  // 加载列表数据
  const loadData = async (params: QueryPickingTaskPageRequest) => {
    setLoading(true);
    try {
      const response = await pickingTaskService.pagePickingTask(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 20 });
  }, []);

  // 搜索
  const handleSearch = (values: any) => {
    searchForm.setFieldsValue(values);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    const createTimeRange = values.createTime;
    const params: QueryPickingTaskPageRequest = {
      currentPage: 1,
      pageSize: pagination.pageSize,
      taskCode: values.taskCode,
      waveNo: values.waveNo,
      taskSource: values.taskSource,
      taskStatus: values.taskStatus,
      pickerAccount: values.pickerAccount,
      createTimeStart: createTimeRange?.[0] ? createTimeRange[0].valueOf() : undefined,
      createTimeEnd: createTimeRange?.[1] ? createTimeRange[1].valueOf() : undefined,
    };
    loadData(params);
  };

  // 重置
  const handleReset = () => {
    searchForm.resetFields();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  // 分页变化
  const handlePageChange = (page: number, pageSize: number) => {
    const values = searchForm.getFieldsValue();
    const createTimeRange = values.createTime;
    const params: QueryPickingTaskPageRequest = {
      currentPage: page,
      pageSize,
      taskCode: values.taskCode,
      waveNo: values.waveNo,
      taskSource: values.taskSource,
      taskStatus: values.taskStatus,
      pickerAccount: values.pickerAccount,
      createTimeStart: createTimeRange?.[0] ? createTimeRange[0].valueOf() : undefined,
      createTimeEnd: createTimeRange?.[1] ? createTimeRange[1].valueOf() : undefined,
    };
    loadData(params);
  };

  // 查看详情
  const handleView = (record: PickingTaskDTO) => {
    setCurrentTaskCode(record.taskCode);
    setDetailVisible(true);
  };

  // 格式化时间
  const formatTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  // 渲染状态Tag
  const renderStatusTag = (status: string, options: any[]) => {
    const option = options.find(opt => opt.value === status);
    if (!option) return status;
    return <Tag color={option.color}>{t(option.label)}</Tag>;
  };

  // 打开指派Drawer
  const handleOpenAssignDrawer = () => {
    const assignableRows = selectedRows.filter(row => canAssignStatuses.includes(row.taskStatus));
    if (assignableRows.length === 0) {
      message.warning(t('picking.pickingTask.list.assign.noTasksSelected'));
      return;
    }
    assignForm.resetFields();
    setAssignDrawerVisible(true);
    loadPickerOptions();
  };

  // 关闭指派Drawer
  const handleCloseAssignDrawer = () => {
    setAssignDrawerVisible(false);
    assignForm.resetFields();
    setPickerOptions([]);
  };

  // 执行指派
  const handleAssign = async () => {
    try {
      const values = await assignForm.validateFields();
      const assignableRows = selectedRows.filter(row => canAssignStatuses.includes(row.taskStatus));
      
      if (assignableRows.length === 0) {
        message.warning(t('picking.pickingTask.list.assign.noTasksSelected'));
        return;
      }

      // 确认弹窗
      Modal.confirm({
        title: t('picking.pickingTask.list.confirm.assignTitle'),
        icon: <ExclamationCircleOutlined />,
        content: t('picking.pickingTask.list.confirm.assignConfirm', { 
          count: assignableRows.length, 
          pickerName: values.pickerName 
        }),
        okText: t('picking.pickingTask.list.confirm.assignOk'),
        cancelText: t('common.cancel'),
        onOk: async () => {
          setAssignLoading(true);
          try {
            const request: AssignPickingTaskRequest = {
              taskCodes: assignableRows.map(row => row.taskCode),
              workerAccount: values.pickerAccount,
              workerName: values.pickerName,
            };
            const response = await pickingTaskService.assignPickingTask(request);
            if (response.success) {
              message.success(t('picking.pickingTask.list.message.batchAssignSuccess', { 
                count: assignableRows.length 
              }));
              handleCloseAssignDrawer();
              setSelectedRowKeys([]);
              setSelectedRows([]);
              loadData({ currentPage: pagination.currentPage, pageSize: pagination.pageSize });
            } else {
              message.error(response.errorMessage || t('common.message.operationFailed'));
            }
          } catch (error) {
            message.error(t('common.message.operationFailed'));
          } finally {
            setAssignLoading(false);
          }
        },
      });
    } catch (error) {
      // 表单验证失败
    }
  };

  // 导出
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const values = searchForm.getFieldsValue();
      const createTimeRange = values.createTime;
      const params: QueryPickingTaskPageRequest = {
        currentPage: 1,
        pageSize: 99999,
        taskCode: values.taskCode,
        waveNo: values.waveNo,
        taskSource: values.taskSource,
        taskStatus: values.taskStatus,
        pickerAccount: values.pickerAccount,
        createTimeStart: createTimeRange?.[0] ? createTimeRange[0].valueOf() : undefined,
        createTimeEnd: createTimeRange?.[1] ? createTimeRange[1].valueOf() : undefined,
      };
      const response = await pickingTaskService.exportPickingTask(params);
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = response.data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success(t('common.message.exportSuccess'));
      } else {
        message.error(response.errorMessage || t('common.message.exportFailed'));
      }
    } catch (error) {
      message.error(t('common.message.exportFailed'));
    } finally {
      setExportLoading(false);
    }
  };

  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: PickingTaskDTO[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRows(newSelectedRows);
    },
    getCheckboxProps: (record: PickingTaskDTO) => ({
      disabled: !canAssignStatuses.includes(record.taskStatus),
    }),
  };

  // 表格列定义
  const columns = [
    {
      title: t('picking.pickingTask.list.table.taskCode'),
      dataIndex: 'taskCode',
      key: 'taskCode',
      width: 180,
      fixed: 'left' as const,
      render: (text: string, record: PickingTaskDTO) => (
        <a onClick={() => handleView(record)}>{text}</a>
      ),
    },
    {
      title: t('picking.pickingTask.list.table.waveNo'),
      dataIndex: 'waveNo',
      key: 'waveNo',
      width: 160,
      render: (text: string | null) => text || '-',
    },
    {
      title: t('picking.pickingTask.list.table.taskSource'),
      dataIndex: 'taskSource',
      key: 'taskSource',
      width: 100,
      render: (source: string) => {
        const option = TaskSourceOptions.find(opt => opt.value === source);
        return option ? t(option.label) : source;
      },
    },
    {
      title: t('picking.pickingTask.list.table.taskStatus'),
      dataIndex: 'taskStatus',
      key: 'taskStatus',
      width: 130,
      render: (status: string) => renderStatusTag(status, PickingTaskStatusOptions),
    },
    {
      title: t('picking.pickingTask.list.table.orderCount'),
      dataIndex: 'orderCount',
      key: 'orderCount',
      width: 100,
    },
    {
      title: t('picking.pickingTask.list.table.skuCount'),
      dataIndex: 'skuCount',
      key: 'skuCount',
      width: 80,
    },
    {
      title: t('picking.pickingTask.list.table.itemCount'),
      dataIndex: 'itemCount',
      key: 'itemCount',
      width: 80,
    },
    {
      title: t('picking.pickingTask.list.table.pickedSkuCount'),
      dataIndex: 'pickedSkuCount',
      key: 'pickedSkuCount',
      width: 100,
    },
    {
      title: t('picking.pickingTask.list.table.pickedItemCount'),
      dataIndex: 'pickedItemCount',
      key: 'pickedItemCount',
      width: 100,
    },
    {
      title: t('picking.pickingTask.list.table.shortageSkuCount'),
      dataIndex: 'shortageSkuCount',
      key: 'shortageSkuCount',
      width: 100,
      render: (count: number) => (
        <span style={{ color: count > 0 ? 'red' : 'inherit' }}>{count}</span>
      ),
    },
    {
      title: t('picking.pickingTask.list.table.shortageItemCount'),
      dataIndex: 'shortageItemCount',
      key: 'shortageItemCount',
      width: 100,
      render: (count: number) => (
        <span style={{ color: count > 0 ? 'red' : 'inherit' }}>{count}</span>
      ),
    },
    {
      title: t('picking.pickingTask.list.table.pickerName'),
      dataIndex: 'pickerName',
      key: 'pickerName',
      width: 120,
      render: (text: string | null) => text || '-',
    },
    {
      title: t('picking.pickingTask.list.table.storeName'),
      dataIndex: 'storeName',
      key: 'storeName',
      width: 120,
    },
    {
      title: t('picking.pickingTask.list.table.startTime'),
      dataIndex: 'startTime',
      key: 'startTime',
      width: 180,
      render: formatTime,
    },
    {
      title: t('picking.pickingTask.list.table.finishTime'),
      dataIndex: 'finishTime',
      key: 'finishTime',
      width: 180,
      render: formatTime,
    },
    {
      title: t('picking.pickingTask.list.table.totalDuration'),
      dataIndex: 'totalDuration',
      key: 'totalDuration',
      width: 100,
      render: (text: string | null) => text || '-',
    },
    {
      title: t('picking.pickingTask.list.table.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      sorter: true,
      render: formatTime,
    },
    {
      title: t('picking.pickingTask.list.table.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (text: any, record: PickingTaskDTO) => (
        <a onClick={() => handleView(record)}>{t('picking.pickingTask.list.actions.view')}</a>
      ),
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 搜索区域 - 使用FilterPanel */}
      <FilterPanel
        form={searchForm}
        onFilter={handleSearch}
        onReset={handleReset}
        title={t('picking.pickingTask.list.title')}
        collapsible={true}
        defaultCollapsed={false}
        loading={loading}
      >
        <Col span={6}>
          <Form.Item name="taskCode" label={t('picking.pickingTask.list.search.taskCode')}>
            <Input placeholder={t('picking.pickingTask.list.search.placeholder.taskCode')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="waveNo" label={t('picking.pickingTask.list.search.waveNo')}>
            <Input placeholder={t('picking.pickingTask.list.search.placeholder.waveNo')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="taskSource" label={t('picking.pickingTask.list.search.taskSource')}>
            <Select
              placeholder={t('picking.pickingTask.list.search.placeholder.taskSource')}
              allowClear
            >
              {TaskSourceOptions.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="taskStatus" label={t('picking.pickingTask.list.search.taskStatus')}>
            <Select
              placeholder={t('picking.pickingTask.list.search.placeholder.taskStatus')}
              allowClear
            >
              {PickingTaskStatusOptions.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="pickerAccount" label={t('picking.pickingTask.list.search.pickerAccount')}>
            <Input placeholder={t('picking.pickingTask.list.search.placeholder.pickerAccount')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="createTime" label={t('picking.pickingTask.list.search.createTime')}>
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder={[t('common.pleaseInput'), t('common.pleaseInput')]}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* 批量操作和表格区域 */}
      <Card style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', marginTop: 24 }} bodyStyle={{ flex: 1, overflow: 'hidden', padding: '0 24px' }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button 
              type="primary" 
              icon={<UserOutlined />}
              onClick={handleOpenAssignDrawer}
              disabled={selectedRowKeys.length === 0}
            >
              {t('picking.pickingTask.list.actions.assign')}
            </Button>
            {selectedRowKeys.length > 0 && (
              <span style={{ color: '#666' }}>
                {t('picking.pickingTask.list.selection.selected', { count: selectedRowKeys.length })}
              </span>
            )}
          </Space>
          <Button
            icon={<ExportOutlined />}
            loading={exportLoading}
            onClick={handleExport}
          >
            {t('common.export')}
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="taskCode"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: pagination.totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('common.pagination.total', { total }),
            onChange: handlePageChange,
          }}
          scroll={{ x: 2270, y: 'calc(100vh - 420px)' }}
        />
      </Card>

      {/* 详情 Drawer */}
      <PickingTaskDetail
        visible={detailVisible}
        taskCode={currentTaskCode}
        onClose={() => {
          setDetailVisible(false);
          setCurrentTaskCode(null);
        }}
      />

      {/* 指派 Drawer */}
      <Drawer
        title={t('picking.pickingTask.list.assign.title')}
        placement="right"
        width={480}
        open={assignDrawerVisible}
        onClose={handleCloseAssignDrawer}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseAssignDrawer}>{t('common.cancel')}</Button>
              <Button type="primary" loading={assignLoading} onClick={handleAssign}>
                {t('picking.pickingTask.list.confirm.assignOk')}
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item
            name="pickerAccount"
            label={t('picking.pickingTask.list.assign.selectPicker')}
            rules={[{ required: true, message: t('picking.pickingTask.list.assign.pickerRequired') }]}
          >
            <Select
              placeholder={t('picking.pickingTask.list.assign.pickerPlaceholder')}
              showSearch
              filterOption={false}
              onSearch={handlePickerSearch}
              notFoundContent={pickerSearching ? <Spin size="small" /> : null}
              options={pickerOptions}
              onChange={(value) => {
                const opt = pickerOptions.find(o => o.value === value);
                assignForm.setFieldsValue({ pickerName: opt?.pickerName || value });
              }}
            />
          </Form.Item>
          <Form.Item name="pickerName" hidden>
            <Input />
          </Form.Item>
        </Form>

        <Alert
          type="info"
          showIcon
          message={t('picking.pickingTask.list.assign.selectedTasks')}
          style={{ marginBottom: 16 }}
        />

        <Table
          dataSource={selectedRows.filter(row => canAssignStatuses.includes(row.taskStatus))}
          rowKey="taskCode"
          size="small"
          pagination={false}
          columns={[
            {
              title: t('picking.pickingTask.list.table.taskCode'),
              dataIndex: 'taskCode',
              key: 'taskCode',
              width: 150,
            },
            {
              title: t('picking.pickingTask.list.table.taskStatus'),
              dataIndex: 'taskStatus',
              key: 'taskStatus',
              width: 100,
              render: (status: string) => renderStatusTag(status, PickingTaskStatusOptions),
            },
          ]}
        />
      </Drawer>
    </div>
  );
};

export default PickingTaskList;
